// src/services/MinhaUnimed/DadosCadastrais/Contato/Contato.js
import axios from 'axios';

export default class ContatoApi {
  /**
   * Construtor para o serviço de Contatos.
   * @param {string} tokenBearer - Token de autenticação Bearer.
   * @param {string} baseUrl - URL base da sua API (ex: 'http://localhost:3000').
   * @param {string | number} unimedId - O ID da Unimed.
   */
  constructor(tokenBearer, baseUrl, unimedId) {
    this.url = `${baseUrl}/api/unimed/${unimedId}/contatos`; // URL base para /contatos (plural)
    this.tokenBearer = tokenBearer;
    this.baseApiUnimedUrl = `${baseUrl}/api/unimed/${unimedId}`; // Uma base mais genérica para construir outras URLs
  }

  /**
   * Extrai a mensagem de erro de uma resposta do Axios.
   * @param {Error} error - O objeto de erro retornado pelo Axios.
   * @returns {string | Error} A string da mensagem de erro ou o objeto de erro original.
   */
  _extractErrorMessage(error) {
    if (error.response && error.response.data && error.response.data.message) {
      return error.response.data.message;
    }
    return error; 
  }

  /**
   * Obtém os dados de contatos.
   * @param {string} args - Argumentos adicionais para a URL (ex: um ID de contato específico).
   * @returns {Promise<Object>} Uma promessa que resolve com a resposta da API.
   */
  getContatos(args = '') {
    return new Promise(async (resolve, reject) => {
      let options = {
        method: 'get',
        url: `${this.url}${args ? '/' + args : ''}`, 
        headers: {
          'Authorization': this.tokenBearer
        }
      };

      try {
        let response = await axios(options);
        resolve(response);
      } catch (err) {
        reject(this._extractErrorMessage(err)); 
      }
    });
  }

  /**
   * Realiza uma requisição PUT para uma URL específica (ex: /contato/{id} ou /cabecalho/{id}).
   * Este método permite que a URL seja totalmente controlada por quem o chama.
   * @param {string} relativePath - O caminho relativo à base da API da Unimed (ex: '/contato/123', '/cabecalho/456').
   * @param {Object} data - Os dados a serem enviados no corpo da requisição.
   * @returns {Promise<Object>} Uma promessa que resolve com a resposta da API.
   */
  put(relativePath, data) {
    return new Promise(async (resolve, reject) => {
      let options = {
        method: 'put',
        url: `${this.baseApiUnimedUrl}${relativePath}`,
        headers: {
          'Authorization': this.tokenBearer,
          'Content-Type': 'application/json'
        },
        data: data
      };

      try {
        let response = await axios(options);
        resolve(response);
      } catch (err) {
        reject(this._extractErrorMessage(err));
      }
    });
  }

  /**
   * Atualiza os dados de contatos (mantendo seu método putContatos original, se ainda for usado em outros lugares).
   * @param {Object} data - Os dados do contato a serem enviados no corpo da requisição.
   * @returns {Promise<Object>} Uma promessa que resolve com a resposta da API.
   */
  putContatos(data) {
    return new Promise(async (resolve, reject) => {
      let options = {
        method: 'put',
        url: this.url,
        headers: {
          'Authorization': this.tokenBearer,
          'Content-Type': 'application/json'
        },
        data: data
      };

      try {
        let response = await axios(options);
        resolve(response);
      } catch (err) {
        reject(this._extractErrorMessage(err));
      }
    });
  }

  /**
   * Exclui dados de contatos.
   * @param {Object} data - Os dados a serem enviados para a exclusão (se o DELETE precisar de corpo).
   * @returns {Promise<Object>} Uma promessa que resolve com a resposta da API.
   */
  delContatos(data = {}) {
    return new Promise(async (resolve, reject) => {
      let options = {
        method: 'delete',
        url: this.url,
        headers: {
          'Authorization': this.tokenBearer
        },
        data: data
      };

      try {
        let response = await axios(options);
        resolve(response);
      } catch (err) {
        reject(this._extractErrorMessage(err));
      }
    });
  }

  /**
   * Adiciona novos dados de contatos (se a API suportar POST para criar novos contatos).
   * @param {Object} data - Os dados do novo contato a serem enviados no corpo da requisição.
   * @returns {Promise<Object>} Uma promessa que resolve com a resposta da API.
   */
  postContatos(data) {
    return new Promise(async (resolve, reject) => {
      let options = {
        method: 'post',
        url: this.url,
        headers: {
          'Authorization': this.tokenBearer
        },
        data: data
      };

      try {
        let response = await axios(options);
        resolve(response);
      } catch (err) {
        reject(this._extractErrorMessage(err));
      }
    });
  }
}